import { useState } from "react";
import { LogOut, User, HelpCircle, FileText, ShieldCheck, Users, Building2, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserProfileModal } from "@/components/UserProfileModal";
import { SupportModal } from "@/components/SupportModal";

const DOCS_URL = "https://docs.kargonode.com";

interface Props {
  collapsed?: boolean;
}

function getInitialsFromName(displayName: string): string {
  const cleaned = displayName.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+|[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

export function UserProfileMenu({ collapsed = false }: Props) {
  const { user, logout, userRole } = useAuth();
  const { isSuperAdmin } = useIsSuperAdmin();
  const navigate = useNavigate();
  const email = user?.email ?? "";

  const [profileOpen, setProfileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  // Fuente de verdad: auth.users.raw_user_meta_data (vía useAuth)
  const metaName =
    ((user?.user_metadata?.full_name as string | undefined) ??
      (user?.user_metadata?.name as string | undefined) ??
      "").trim();
  const fallbackName = email.split("@")[0] ?? "";
  const fullName = metaName || fallbackName;
  const initials = getInitialsFromName(fullName);

  const avatarEl = (
    <Avatar className="h-8 w-8 border border-border">
      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-2.5 w-full rounded-md px-2 py-2 text-left transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
            collapsed ? "justify-center" : ""
          }`}
        >
          {avatarEl}
          {!collapsed && (
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-medium text-foreground truncate">
                {fullName}
              </span>
              <span className="text-[10px] text-muted-foreground truncate tabular-nums">
                {email}
              </span>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side={collapsed ? "right" : "top"}
        align="start"
        className="w-56 rounded-md"
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-2.5">
            {avatarEl}
            <div className="flex flex-col min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {fullName}
              </p>
              <p className="text-xs text-muted-foreground truncate tabular-nums">
                {email}
              </p>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem className="rounded-md" onSelect={() => setProfileOpen(true)}>
            <User className="mr-2 h-4 w-4" />
            Perfil
          </DropdownMenuItem>
          <DropdownMenuItem className="rounded-md" onClick={() => navigate("/dashboard/equipo")}>
            <Users className="mr-2 h-4 w-4" />
            Mi Equipo
          </DropdownMenuItem>
          <DropdownMenuItem className="rounded-md" onClick={() => navigate("/admin/clientes")}>
            <Building2 className="mr-2 h-4 w-4" />
            Gestión de Clientes
          </DropdownMenuItem>
          {(isSuperAdmin || userRole === "ADMIN") && (
            <DropdownMenuItem className="rounded-md" onClick={() => navigate("/admin")}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              {isSuperAdmin ? "Consola Master" : "Administración"}
            </DropdownMenuItem>
          )}
          {isSuperAdmin && (
            <DropdownMenuItem className="rounded-md" onClick={() => navigate("/admin/performance")}>
              <Activity className="mr-2 h-4 w-4" />
              DB Performance
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem
            className="rounded-md"
            onSelect={() => window.open(DOCS_URL, "_blank", "noopener,noreferrer")}
          >
            <FileText className="mr-2 h-4 w-4" />
            Documentación
          </DropdownMenuItem>
          <DropdownMenuItem className="rounded-md" onSelect={() => setSupportOpen(true)}>
            <HelpCircle className="mr-2 h-4 w-4" />
            Ayuda
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={logout}
          className="rounded-md text-destructive focus:text-destructive focus:bg-destructive/10"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Cerrar Sesión
        </DropdownMenuItem>
      </DropdownMenuContent>

      <UserProfileModal open={profileOpen} onOpenChange={setProfileOpen} />
      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
    </DropdownMenu>
  );
}
